<script>
import { ref, onBeforeMount, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import {
  getRestoPaymentSettingFn,
  createOrderPIFn,
  placeCardOrderSCAFn,
  confirmOrderPIFn
} from '@/services/api/restApiCalls'
import { StripeElements, StripeElement } from 'vue-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

export default {
  components: {
    StripeElements,
    StripeElement
  },

  setup() {
    const router = useRouter()
    const restoStore = useRestoGLobalStore()
    const cartStore = useCartGLobalStore()

    let stripeKey = ref('')
    let stripeAccountID = ref('')
    const stripeLoaded = ref(false)
    const cardErrorMsg = ref('')
    const clientSecret = ref('')
    const elms = ref()
    const elementsInstance = ref(null)
    const payButtonDisabled = ref(false)
    let is_card_processing = ref(false)
    
    // Initialize appearance for Stripe Elements
    const appearance = ref({
      theme: 'stripe',
      variables: {
        colorPrimary: '#6d28d9',
      }
    })

    const instanceOptions = ref({
      // https://stripe.com/docs/js/initializing#init_stripe_js-options
    })

    const elementsOptions = computed(() => {
      // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
      // Use order_total as per user instruction
      const total = cartStore.CART_DATA?.order_total || 0
      const amountInCents = Math.round(total * 100)
      
      const options = {
        appearance: appearance.value,
        mode: 'payment',
        amount: amountInCents > 0 ? amountInCents : 1, // Fallback to avoid "amount must be greater than 0" error during init if 0
        currency: 'gbp', // Default to GBP, could be dynamic
        paymentMethodCreation: 'manual', // Required for createPaymentMethod with Payment Element
      }
      return options
    })

    const paymentElementOptions = computed(() => {
      const isSecure = window.location.protocol === 'https:'
      
      // Use 'if_required' to only show address fields when strictly needed by the payment method/country
      const baseOptions = {
        layout: 'tabs',
        fields: {
          billingDetails: {
            address: 'if_required'
          }
        }
      }

      // Disable wallets on non-secure connections (like localhost HTTP) to avoid errors
      if (!isSecure) {
         return {
           ...baseOptions,
           wallets: {
             applePay: 'never',
             googlePay: 'never'
           }
         }
      }
      
      return baseOptions
    })

    // Add dynamic styles based on theme
    const updateStripeElementsTheme = () => {
      // ... (same as before) ...
      const isDarkMode = document.documentElement.classList.contains('dark')
      if (isDarkMode) {
        appearance.value = {
          theme: 'night',
          variables: {
            colorPrimary: '#8b5cf6', // brighter violet for dark mode
            colorText: '#e5e7eb',
            colorBackground: '#374151',
            colorDanger: '#ef4444',
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            spacingUnit: '4px',
            borderRadius: '6px',
          }
        }
      } else {
        appearance.value = {
          theme: 'stripe',
          variables: {
            colorPrimary: '#6d28d9',
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            spacingUnit: '4px',
            borderRadius: '6px',
          }
        }
      }
    }

    const handleStripePayment = async () => {
      if (is_card_processing.value) {
        return
      }
      
      is_card_processing.value = true
      
      const stripeInstance = elms.value.instance
      const elements = elementsInstance.value
      
      // Submit the form first - required for Payment Element with deferred intent
      if (elements && typeof elements.submit === 'function') {
        const { error: submitError } = await elements.submit()
        
        if (submitError) {
          cardErrorMsg.value = submitError.message
          payButtonDisabled.value = true
          is_card_processing.value = false
          return
        }
      }

      // Create PaymentMethod using the stripe instance
      stripeInstance
        .createPaymentMethod({
          elements,
          params: {}
        })
        .then(async function (result) {
          if (result?.error) {
            cardErrorMsg.value = result?.error?.message
            payButtonDisabled.value = true
            is_card_processing.value = false
            return
          }
          
          // Check for Amex card and block it
          const cardBrand = result?.paymentMethod?.card?.brand
          if (cardBrand === 'amex') {
            cardErrorMsg.value = 'Amex card is not supported'
            payButtonDisabled.value = true
            is_card_processing.value = false
            return
          }
          
          let paymentMethodId = result?.paymentMethod?.id

          try {
            // Create Order and PI on backend
            const place_order = await placeCardOrderSCAFn(restoStore?.RESTO_ID, paymentMethodId)
            
            if (!place_order || place_order?.error) {
              cardErrorMsg.value = place_order?.error?.message || 'Failed to create order. Please try again.'
              is_card_processing.value = false
              return
            }
            
            let orderStatus = place_order?.order_status
            
            if (orderStatus == 'REQUIRES_ACTION') {
              let orderId = place_order?.order_id
              let action_client_secret = place_order?.client_secret
              
              // Handle next action (3DS etc)
              const stripe = elms.value.instance
              const actionResult = await stripe.handleNextAction({ clientSecret: action_client_secret })
              
              if (actionResult.error) {
                cardErrorMsg.value = actionResult.error.message
                is_card_processing.value = false
                return
              }
              
              const confirm_pi_data = await confirmOrderPIFn(restoStore?.RESTO_ID, orderId)
              if (confirm_pi_data?.status == 200) {
                let confirmedOrderId = confirm_pi_data?.order_id
                is_card_processing.value = false
                router.push({
                  name: 'order_status',
                  params: { order_id: `${confirmedOrderId}` }
                })
                cartStore.CART_DATA = ''
              } else {
                cardErrorMsg.value = 'Payment confirmation failed. Please contact support.'
                is_card_processing.value = false
              }
            } else if (place_order?.order_id) {
              // Payment succeeded without additional action
              is_card_processing.value = false
              router.push({
                name: 'order_status',
                params: { order_id: `${place_order?.order_id}` }
              })
              cartStore.CART_DATA = ''
            } else {
              cardErrorMsg.value = 'Unexpected response from server. Please try again.'
              is_card_processing.value = false
            }
          } catch (err) {
            console.error('Payment error:', err)
            cardErrorMsg.value = err?.message || 'An error occurred during payment. Please try again.'
            is_card_processing.value = false
          }
        })
        .catch(function (err) {
          console.error('createPaymentMethod error:', err)
          cardErrorMsg.value = err?.message || 'Failed to process payment. Please try again.'
          is_card_processing.value = false
        })
    }

    onBeforeMount(async () => {
      is_card_processing.value = false

      // NO initial PI creation needed for Deferred Intent flow
      
      let payment_settings = await getRestoPaymentSettingFn(restoStore?.RESTO_ID)
      // ... (rest of the setup)
      
      // retry to get payment settings
      if (!payment_settings?.public_key) {
        payment_settings = await getRestoPaymentSettingFn(restoStore?.RESTO_ID)
      }

      if (payment_settings?.public_key) {
        stripeKey.value = payment_settings?.public_key
      }
      if (payment_settings?.account_id) {
        stripeAccountID.value = payment_settings?.account_id
        instanceOptions.value.stripeAccount = stripeAccountID.value
      }

      if (stripeKey.value) {
        updateStripeElementsTheme()
        const stripePromise = loadStripe(stripeKey.value)
        stripePromise.then(() => {
          stripeLoaded.value = true
        })
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        mediaQuery.addEventListener('change', updateStripeElementsTheme)
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'class' && 
                mutation.target === document.documentElement) {
              updateStripeElementsTheme()
            }
          })
        })
        observer.observe(document.documentElement, { attributes: true })
      } else {
        router.push({
          name: 'menu',
          params: { menu_slug: restoStore?.MENU_URL }
        })
      }
    })
    
    const handleChange = (event) => {
      // Payment Element 'change' event
      // Note: Payment Element does NOT expose card brand in change events
      // Amex detection is handled after createPaymentMethod in handleStripePayment
      payButtonDisabled.value = !event.complete
      if (event.error) {
        cardErrorMsg.value = event.error.message
      } else {
        cardErrorMsg.value = ''
      }
    }
    
    // Store elements instance when it becomes available
    const onElementsReady = (elements) => {
      elementsInstance.value = elements
    }
    return {
      restoStore,
      cartStore,
      stripeLoaded,
      stripeKey,
      stripeAccountID,
      cardErrorMsg,
      elms,
      instanceOptions,
      elementsOptions,
      paymentElementOptions,
      payButtonDisabled,
      handleStripePayment,
      handleChange,
      onElementsReady
    }
  }
}
</script>

<template>
  <div class="stripe-payment-element-wrapper">
    <StripeElements
      v-if="stripeLoaded"
      v-slot="{ elements, instance }"
      ref="elms"
      :stripe-key="stripeKey"
      :instance-options="instanceOptions"
      :elements-options="elementsOptions"
    >
      <StripeElement
        type="payment"
        :elements="elements"
        :options="paymentElementOptions"
        @change="handleChange($event)"
        @ready="onElementsReady(elements)"
        class="stripe-payment-element"
      ></StripeElement>
    </StripeElements>
    <div v-else class="p-4 text-center text-gray-500 dark:text-gray-400">
        Loading payment details...
    </div>

    <div
      v-if="cardErrorMsg"
      class="text-sm font-medium text-red-600 dark:text-red-400 mt-2"
    >
      {{ cardErrorMsg }}
    </div>
  </div>
</template>

<style>
/* Container for the Payment Element - ensures proper spacing and layout */
.stripe-payment-element-wrapper {
  position: relative;
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

/* The Payment Element container */
.stripe-payment-element-wrapper .stripe-payment-element {
  width: 100%;
}

/* Override any inherited StripeElement styles that might cause issues */
.stripe-payment-element-wrapper .StripeElement {
  height: auto !important;
  padding: 0;
  border: none;
  background: transparent;
}
</style>
